import React from "react"
import { NavLink as RouterLink } from "react-router-dom"
import clsx from "clsx"
import PropTypes from "prop-types"
import { Button, Collapse, ListItem, makeStyles } from "@material-ui/core"
import ExpandMoreIcon from "@material-ui/icons/ExpandMore"
import ExpandLessIcon from "@material-ui/icons/ExpandLess"

const useStyles = makeStyles(theme => ({
  item: {
    display: "block",
    paddingTop: 0,
    paddingBottom: 0,
  },
  itemLeaf: {
    display: "flex",
    paddingTop: 0,
    paddingBottom: 0,
  },
  button: {
    color: "#ffffff",
    padding: "5px 8px",
    justifyContent: "flex-start",
    textTransform: "none",
    letterSpacing: 0,
    width: "100%",
  },
  buttonLeaf: {
    color: "#ffffff",
    padding: "10px 8px",
    justifyContent: "flex-start",
    textTransform: "none",
    letterSpacing: 0,
    width: "100%",
    fontWeight: theme.typography.fontWeightRegular,
    "&.depth-0": {
      "& $title": {
        fontWeight: theme.typography.fontWeightMedium,
      },
    },
  },
  icon: {
    display: "flex",
    alignItems: "center",
    marginRight: theme.spacing(1),
    color: "#ffffff",
    fontSize: "1.4em",
  },
  title: {
    marginRight: "auto",
    color: "#ffffff",
    fontSize: "1em",
  },
  active: {
    backgroundColor: "#3a5f91",
    borderRadius: "8px",
    "& $title": {
      color: "#ffffff",
      fontWeight: theme.typography.fontWeightMedium,
    },
    "& $icon": {
      color: "#ffffff",
    },
    "&:hover": {
      backgroundColor: "#3a5f91",
    },
  },
  activeSub: {
    backgroundColor: "#2c4875",
    borderRadius: "8px",
    "& $title": {
      color: "#ffffff",
      fontWeight: theme.typography.fontWeightMedium,
    },
    "& $icon": {
      color: "#ffffff",
    },
    "&:hover": {
      backgroundColor: "#2c4875",
    },
  },
  parentActive: {
    backgroundColor: "#3a5f91",
    borderRadius: "8px",
    "&:hover": {
      backgroundColor: "#3a5f91",
    },
  },
}))

const NavItem = ({
  children,
  className,
  depth,
  href,
  icon: Icon,
  info: Info,
  title,
  open,
  active,
  onToggle,
  ...rest
}) => {
  const classes = useStyles()

  let paddingLeft = 8

  if (depth > 0) {
    paddingLeft = 32 + 8 * depth
  }

  const style = { paddingLeft }
  const isSubMenu = depth > 0

  if (children) {
    return (
      <ListItem
        className={clsx(
          classes.item,
          className,
          active && classes.parentActive
        )}
        disableGutters
        {...rest}
      >
        <Button
          className={clsx(classes.button, active && classes.parentActive)}
          onClick={onToggle}
          style={style}
        >
          {Icon && <Icon className={classes.icon} size="12" />}
          <span className={classes.title}>{title}</span>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Button>
        <Collapse in={open} timeout="auto" unmountOnExit>
          {children}
        </Collapse>
      </ListItem>
    )
  }

  return (
    <ListItem
      className={clsx(classes.itemLeaf, className)}
      disableGutters
      {...rest}
    >
      <Button
        activeClassName={isSubMenu ? classes.activeSub : classes.active}
        className={clsx(
          classes.buttonLeaf,
          `depth-${depth}`,
          active && (isSubMenu ? classes.activeSub : classes.active)
        )}
        component={RouterLink}
        exact
        style={style}
        to={href}
      >
        {Icon && <Icon className={classes.icon} size="20" />}
        <span className={classes.title}>{title}</span>
        {Info && <Info />}
      </Button>
    </ListItem>
  )
}

NavItem.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  icon: PropTypes.elementType,
  info: PropTypes.elementType,
  onToggle: PropTypes.func,
  open: PropTypes.bool,
  title: PropTypes.string.isRequired,
}

NavItem.defaultProps = {
  active: false,
  children: "",
  className: "",
  href: "",
  icon: undefined,
  info: undefined,
  onToggle: undefined,
  open: false,
}

export default NavItem
